import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Loader2, Lock, ExternalLink, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

const formatUSD = (cents, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format((cents || 0) / 100);

const FILTERS = ["All", "Music", "Books", "Digital Resources", "Merchandise"];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (image_id) => image_id ? `${BACKEND}/api/store/images/${image_id}` : null;

function ProductImage({ image_id, title, image_url }) {
  const url = image_url || imgUrl(image_id);
  if (url) {
    return <img src={url} alt={title} className="w-full h-56 object-cover" loading="lazy" />;
  }
  return (
    <div className="w-full h-56 flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--burgundy) 0%, var(--obsidian) 100%)" }}>
      <Sparkles className="w-10 h-10" style={{ color: "var(--gold-soft)" }} />
    </div>
  );
}

export default function Store() {
  const { user } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [buying, setBuying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "All" ? "" : `?category=${encodeURIComponent(filter)}`;
      const { data } = await api.get(`/store/products${q}`);
      setProducts(data || []);
    } catch { toast.error("Could not load the store."); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const buy = async (p) => {
    if (p.product_type === "PRINTIFY_PRODUCT") {
      window.location.assign(`/store/merch/${p.id}`);
      return;
    }
    if (p.product_type === "EXTERNAL_PRODUCT") {
      window.open(p.external_url, "_blank", "noopener,noreferrer");
      return;
    }
    if (!user) {
      toast("Please sign in to purchase.");
      return;
    }
    setBuying(p.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        product_id: p.id, quantity: 1, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBuying(null);
    }
  };

  const featured = products.filter((p) => p.featured);
  const rest = products.filter((p) => !p.featured);

  return (
    <div>
      <section className="relative overflow-hidden" style={{ background: "var(--surface-deep)" }}>
        <div className="grain absolute inset-0" />
        <div className="bloom absolute inset-0 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-16 relative" style={{ color: "var(--bg-cream)" }}>
          <div className="eyebrow mb-3" style={{ color: "var(--gold-soft)" }}>Temple Store</div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            Small offerings to carry the Temple with you.
          </h1>
          <p className="mt-4 max-w-2xl opacity-85 text-sm sm:text-base leading-relaxed">
            Books, recordings, and digital resources for the contemplative life.
            Each offering supports the Temple's ongoing work.
          </p>
          {user?.role === "ADMINISTRATOR" ? (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs" style={{ background: "rgba(255,255,255,0.08)", color: "var(--gold-soft)" }} data-testid="store-test-mode-banner">
              <Lock className="w-3.5 h-3.5" /> Admin only · Payments in TEST MODE · card 4242 4242 4242 4242 · any future expiry · any CVC
            </div>
          ) : (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs" style={{ background: "rgba(255,255,255,0.08)", color: "var(--gold-soft)" }} data-testid="store-secure-checkout-note">
              <Lock className="w-3.5 h-3.5" /> Secure checkout by Stripe
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <div className="flex flex-wrap gap-2" data-testid="store-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`store-filter-${f.toLowerCase().replace(/\s+/g, "-")}-btn`}
              onClick={() => setFilter(f)}
              className="rounded-full px-4 py-1.5 text-xs font-medium tracking-wide border transition-colors"
              style={{
                background: filter === f ? "var(--burgundy)" : "transparent",
                color: filter === f ? "var(--bg-cream)" : "var(--burgundy)",
                borderColor: filter === f ? "var(--burgundy)" : "var(--border-cream)",
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24 pt-4">
        {loading ? (
          <div className="text-center py-24" style={{ color: "var(--text-muted)" }}>
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
            <div className="text-sm">Loading offerings…</div>
          </div>
        ) : products.length === 0 ? (
          <div className="rounded-2xl border p-12 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }} data-testid="store-empty">
            <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: "var(--gold-dark)" }} />
            <p className="font-serif text-lg mb-1" style={{ color: "var(--burgundy)" }}>The shelves are being prepared.</p>
            <p className="text-sm">New offerings are on their way. Please return soon.</p>
          </div>
        ) : (
          <>
            {featured.length > 0 && (
              <div className="mb-10" data-testid="store-featured-row">
                <div className="eyebrow mb-3">Featured</div>
                <div className="grid md:grid-cols-2 gap-6">
                  {featured.map((p) => <FeaturedCard key={p.id} p={p} onBuy={buy} buying={buying} />)}
                </div>
              </div>
            )}
            {rest.length > 0 && (
              <div>
                {featured.length > 0 && <div className="eyebrow mb-3">All offerings</div>}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((p) => <ProductCard key={p.id} p={p} onBuy={buy} buying={buying} />)}
                </div>
              </div>
            )}
          </>
        )}

        {!user && products.length > 0 && (
          <div className="mt-10 rounded-xl border p-5 text-sm text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold-soft)", color: "var(--text-obsidian)" }}>
            You'll be asked to <Link to="/login" className="ink-link font-medium" style={{ color: "var(--burgundy)" }}>sign in</Link> before completing a digital purchase, so your download stays tied to your account.
          </div>
        )}
      </section>
    </div>
  );
}

function ProductCard({ p, onBuy, buying }) {
  const isExternal = p.product_type === "EXTERNAL_PRODUCT";
  return (
    <article
      data-testid={`store-product-${p.id}`}
      className="rounded-2xl border overflow-hidden flex flex-col lift"
      style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}
    >
      <ProductImage image_id={p.image_id} image_url={p.image_url} title={p.title} />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <span className="eyebrow text-[9px]">{p.category}</span>
          {isExternal && <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>· External</span>}
        </div>
        <h3 className="font-serif text-lg leading-snug mb-1" style={{ color: "var(--burgundy)" }}>{p.title}</h3>
        {p.subtitle && <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>{p.subtitle}</p>}
        <div className="mt-auto pt-3 flex items-center justify-between">
          <div className="font-serif text-xl" style={{ color: "var(--burgundy-deep)" }} data-testid={`store-price-${p.id}`}>
            {isExternal ? "View on retailer" : `${p.price_from ? "from " : ""}${formatUSD(p.price_cents, p.currency)}`}
          </div>
          <button
            data-testid={`store-buy-${p.id}-btn`}
            onClick={() => onBuy(p)}
            disabled={buying === p.id}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium disabled:opacity-60"
            style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
          >
            {buying === p.id ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> …</>
            ) : isExternal ? (
              <><ExternalLink className="w-3.5 h-3.5" /> Open</>
            ) : p.product_type === "PRINTIFY_PRODUCT" ? (
              <><ShoppingBag className="w-3.5 h-3.5" /> Choose options</>
            ) : (
              <><ShoppingBag className="w-3.5 h-3.5" /> Buy</>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}

function FeaturedCard({ p, onBuy, buying }) {
  const isExternal = p.product_type === "EXTERNAL_PRODUCT";
  return (
    <article
      data-testid={`store-product-${p.id}`}
      className="rounded-2xl border overflow-hidden grid sm:grid-cols-5 lift"
      style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold-soft)" }}
    >
      <div className="sm:col-span-2">
        <ProductImage image_id={p.image_id} title={p.title} />
      </div>
      <div className="sm:col-span-3 p-6 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <span className="eyebrow text-[9px]" style={{ color: "var(--gold-dark)" }}>Featured · {p.category}</span>
          {isExternal && <span className="text-[9px] font-mono uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>· External</span>}
        </div>
        <h3 className="font-serif text-2xl leading-snug mb-2" style={{ color: "var(--burgundy)" }}>{p.title}</h3>
        {p.subtitle && <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>{p.subtitle}</p>}
        {p.description && <p className="text-sm leading-relaxed line-clamp-3 mb-4" style={{ color: "var(--text-obsidian)" }}>{p.description}</p>}
        <div className="mt-auto pt-2 flex items-center justify-between gap-3">
          <div className="font-serif text-2xl" style={{ color: "var(--burgundy-deep)" }} data-testid={`store-price-${p.id}`}>
            {isExternal ? "View on retailer" : formatUSD(p.price_cents, p.currency)}
          </div>
          <button
            data-testid={`store-buy-${p.id}-btn`}
            onClick={() => onBuy(p)}
            disabled={buying === p.id}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60"
            style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
          >
            {buying === p.id ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> …</>
            ) : isExternal ? (
              <><ExternalLink className="w-4 h-4" /> Open</>
            ) : (
              <><ShoppingBag className="w-4 h-4" /> Buy</>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}
